/**
 * Challenge Detail Screen
 * Uses React Query with placeholderData from the list cache for instant open;
 * shows "Loading more details..." while background fetch runs when data came from placeholder.
 */

import { useEffect, useMemo } from 'react';
import { View, ActivityIndicator, Text, Pressable, Platform } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useChallengeDetailQuery } from '../../../src/challenges/hooks/useChallengeDetailQuery';
import { ChallengeDetailView } from '../../../components/challenges/ChallengeDetailView';
import type { ChallengeWithStats } from '../../../types/challenges';
import type { Match } from '../../../types/challenges';
import { challengeDetailScreenStyles } from '../../../src/challenges/styles/challengeDetailScreen.styles';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics';

type ChallengeWithDisplayMaps = ChallengeWithStats & {
  participantDisplayNamesById?: Record<string, string>;
  participantAvatarsById?: Record<string, string>;
};

export default function ChallengeDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  const { track } = useAnalytics();

  useEffect(() => {
    if (id) track(ANALYTICS_EVENTS.CHALLENGE_VIEWED, { challenge_id: id });
  }, [id, track]);
  const {
    data: challenge,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
  } = useChallengeDetailQuery(id);

  const users = useMemo(() => {
    if (!challenge) return [];
    const names = (challenge as ChallengeWithDisplayMaps).participantDisplayNamesById ?? {};
    const avatars = (challenge as ChallengeWithDisplayMaps).participantAvatarsById ?? {};
    const ids = new Set(challenge.participantIds ?? []);
    if (challenge.creatorId && !ids.has(challenge.creatorId)) ids.add(challenge.creatorId);
    return Array.from(ids).map((userId) => ({
      id: userId,
      name: names[userId] ?? userId,
      avatar: avatars[userId],
    }));
  }, [challenge]);

  const matches = useMemo((): Match[] => {
    if (!challenge?.picks?.length) return [];
    const seen = new Set<string>();
    const list: Match[] = [];
    const order = challenge.matchIds ?? [];
    for (const matchId of order) {
      const pick = challenge.picks.find((p) => p.matchId === matchId);
      const m = pick?.match;
      if (m && typeof m === 'object' && 'id' in m && !seen.has((m as Match).id)) {
        seen.add((m as Match).id);
        list.push(m as Match);
      }
    }
    challenge.picks.forEach((p) => {
      const m = p.match;
      if (m && typeof m === 'object' && 'id' in m && !seen.has((m as Match).id)) {
        seen.add((m as Match).id);
        list.push(m as Match);
      }
    });
    return list;
  }, [challenge]);

  const styles = useMemo(() => challengeDetailScreenStyles, []);
  const showLoadingMoreDetails = isPlaceholderData && isFetching && challenge;

  if (!isLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (!isSignedIn) {
    router.replace('/sign-in');
    return null;
  }

  if (!id) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>Missing challenge ID</Text>
        <Pressable
          onPress={() => router.back()}
          style={({ pressed, hovered }) => [
            styles.errorBackButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.errorBackButtonPressed,
            hovered && Platform.OS === 'web' && styles.errorBackButtonHover,
          ]}
        >
          <ArrowLeft size={24} color="#fff" />
          <Text style={styles.backButtonText}>Back</Text>
        </Pressable>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (error || (!challenge && !isLoading)) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.errorText}>
          {error ? (error instanceof Error ? error.message : 'Failed to load challenge') : 'Challenge not found'}
        </Text>
        <Pressable
          onPress={() => router.back()}
          style={({ pressed, hovered }) => [
            styles.errorBackButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.errorBackButtonPressed,
            hovered && Platform.OS === 'web' && styles.errorBackButtonHover,
          ]}
        >
          <ArrowLeft size={24} color="#fff" />
          <Text style={styles.backButtonText}>Back</Text>
        </Pressable>
      </View>
    );
  }

  if (!challenge) {
    return null;
  }

  return (
    <View style={styles.screenContainer}>
      {showLoadingMoreDetails && (
        <View style={styles.loadingMoreDetailsBar}>
          <ActivityIndicator size="small" />
          <Text style={styles.loadingMoreDetailsText}>Loading more details…</Text>
        </View>
      )}
      <Pressable
        onPress={() => router.back()}
        style={({ pressed, hovered }) => [
          styles.headerBackButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.headerBackButtonPressed,
          hovered && Platform.OS === 'web' && styles.headerBackButtonHover,
        ]}
        testID="challenge-detail-back"
      >
        <ArrowLeft size={24} />
      </Pressable>
      <ChallengeDetailView
        challenge={challenge as ChallengeWithDisplayMaps}
        picks={challenge.picks}
        users={users}
        matches={matches}
      />
    </View>
  );
}
